import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useReservationTimeSlots } from "./useReservationTimeSlots";
import { useUserReservations } from "./useUserReservations";

interface TimeSlot {
  id: string;
  start_time: string;
  end_time: string;
  available: boolean;
  is_reserved: boolean;
}

export const useAvailableTimeSlots = (itemId: string | null, selectedDate: string | null) => {
  const { data: timeSlots } = useReservationTimeSlots(itemId);
  const { data: userReservations } = useUserReservations();

  return useQuery({
    queryKey: ['available-time-slots', itemId, selectedDate],
    queryFn: async (): Promise<TimeSlot[]> => {
      console.log('🎯 [useAvailableTimeSlots] Starting with:', { itemId, selectedDate });
      console.log('📦 [useAvailableTimeSlots] TimeSlots received:', timeSlots);
      
      if (!itemId || !selectedDate || !timeSlots) {
        console.log('⚠️ [useAvailableTimeSlots] Missing required data:', { itemId, selectedDate, timeSlots });
        return [];
      }

      // Parse date correctly to avoid timezone issues
      // Use manual parsing to ensure correct day of week calculation
      const [year, month, day] = selectedDate.split('-').map(Number);
      const targetDate = new Date(year, month - 1, day); // month is 0-indexed in JavaScript
      const dayOfWeek = targetDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
      
      console.log('📅 [useAvailableTimeSlots] Date analysis:', {
        selectedDate,
        parsedComponents: { year, month, day },
        targetDate: targetDate.toDateString(),
        dayOfWeek,
        dayName: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayOfWeek]
      });
      
      // PostgreSQL EXTRACT(DOW) returns: 0=Sunday, 1=Monday, 2=Tuesday, ... 6=Saturday
      // JavaScript getDay() returns: 0=Sunday, 1=Monday, 2=Tuesday, ... 6=Saturday
      // They match! No conversion needed
      const pgDayOfWeek = dayOfWeek;

      console.log('🔄 [useAvailableTimeSlots] Available recurring slots to filter:', timeSlots.recurring);
      
      // Get recurring slots for this day of week
      const recurringSlots = timeSlots.recurring?.filter(slot => {
        const matches = slot.day_of_week === pgDayOfWeek && slot.is_available;
        console.log('🔍 [useAvailableTimeSlots] Checking slot:', {
          slot,
          slotDayOfWeek: slot.day_of_week,
          targetDayOfWeek: pgDayOfWeek,
          isAvailable: slot.is_available,
          matches
        });
        return matches;
      }) || [];

      console.log('✅ [useAvailableTimeSlots] Filtered recurring slots:', recurringSlots);

      // Get specific date slots for this exact date
      const specificSlots = timeSlots.specific?.filter(slot => 
        slot.specific_date === selectedDate && slot.is_available
      ) || [];

      console.log('📅 [useAvailableTimeSlots] Specific slots for date:', specificSlots);

      // Combine slots, giving priority to specific date slots
      const allSlots = [...specificSlots];
      
      // Add recurring slots that don't conflict with specific ones
      recurringSlots.forEach(recurringSlot => {
        const hasConflict = specificSlots.some(specificSlot => 
          specificSlot.start_time === recurringSlot.start_time &&
          specificSlot.end_time === recurringSlot.end_time
        );
        
        if (!hasConflict) {
          allSlots.push(recurringSlot);
        }
      });

      console.log('🔗 [useAvailableTimeSlots] Combined slots:', allSlots);

      // Check existing reservations to mark slots as reserved
      const { data: existingReservations } = await supabase
        .from('reservations')
        .select('start_time, end_time')
        .eq('item_id', itemId)
        .eq('reservation_date', selectedDate)
        .in('status', ['confirmed', 'pending']);

      console.log('🚫 [useAvailableTimeSlots] Existing reservations:', existingReservations);

      // Map to TimeSlot interface with reservation status
      const availableSlots = allSlots.map(slot => ({
        id: slot.id,
        start_time: slot.start_time,
        end_time: slot.end_time,
        available: slot.is_available,
        is_reserved: existingReservations?.some(reservation =>
          reservation.start_time === slot.start_time &&
          reservation.end_time === slot.end_time
        ) || false
      }));

      console.log('🎯 [useAvailableTimeSlots] Final available slots:', availableSlots);

      // Sort by start time
      return availableSlots.sort((a, b) => a.start_time.localeCompare(b.start_time));
    },
    enabled: !!itemId && !!selectedDate && !!timeSlots,
  });
};